/* Copyright 2012-2018 The MathWorks, Inc. */

define([
    "dojo/_base/declare",
    "dojo/aspect",
    "dojo/dom-construct",
    "dojo/on",
    "dojo/dnd/Source",
    "dojo/dnd/Target",
    "dijit/registry",
    "dijit/_WidgetBase",
    "dijit/_TemplatedMixin",
    "dijit/_Container",
    "dojo/i18n!l10n/nls/toolstrip",
    "mw-dnd/DnDUtils",
    "dojo/text!./templates/GalleryCategory.html",
    "../../constants/gallery/GalleryViewConstants",
    "../_WidgetMixin",
    "./GalleryItemAvatar"
], function (declare, aspect, domConstruct, on, Source, Target, registry, _WidgetBase, _TemplatedMixin, _Container,
             toolstripL10n, DnDUtils, template, GalleryViewConstants, _WidgetMixin, GalleryItemAvatar) {

    var CATEGORY_ICONS = [
        "",
        "icon_favorite_category_16"
    ];

    return declare([_WidgetBase, _TemplatedMixin, _Container, _WidgetMixin], {

        templateString: template,
        galleryItemRowCount: 1,
        icon: "",
        hideWhenEmpty: true,
        dndEnabled: false,

        /** @lends CategoryWidget# */

        /**
         * Represents a Gallery Category Widget. The Category can be rendered to two types of
         * views depending on the current Gallery Popup view: Icon or List.
         */

        /**
         * @override
         */
        postCreate: function () {
            var that = this;

            this.inherited(arguments);

            this._moveToTopButtonClickHandler = function () {
                // The moveTopButtonNode of the previous Category needs to be focused after this category is moved
                // to the top.
                var categories = that.getParent().getChildren();
                var currentIndex = categories.indexOf(that);
                var nodeToBeFocused;

                // g1917390: Searching in the gallery popup might filter some categories. The moveTopButtonNode to
                // be focused should be of one of the previous categories that are currently visible in the gallery
                // popup.
                for (var i = currentIndex - 1; i >= 0; i--) {
                    if (!categories[i].domNode.classList.contains("hideFromView")) {
                        nodeToBeFocused = categories[i].moveTopButtonNode;
                        break;
                    }
                }

                // If no moveTopButtonNode is found to focus, then focus the moveBottomButtonNode of the same category.
                if (!nodeToBeFocused) {
                    nodeToBeFocused = categories[currentIndex].moveBottomButtonNode;
                }

                that.categoryMoved({
                    action: "Top",
                    itemId: that.id,
                    newIndex: 0,
                    originator: {source: "UI"}
                });

                nodeToBeFocused.focus();
            };

            this._moveToBottomButtonClickHandler = function () {
                // The moveBottomButtonNode of the next Category needs to be focused after this category is moved to
                // the bottom.
                var categories = that.getParent().getChildren();
                var currentIndex = categories.indexOf(that);
                var nodeToBeFocused;

                // g1917390: Searching in the gallery popup might filter some categories. The moveBottomButtonNode to
                // be focused should be of one of the next categories that are currently visible in the gallery
                // popup.
                for (var i = currentIndex + 1; i < categories.length; i++) {
                    if (!categories[i].domNode.classList.contains("hideFromView")) {
                        nodeToBeFocused = categories[i].moveBottomButtonNode;
                        break;
                    }
                }

                // If no moveBottomButtonNode is found to focus, then focus the moveTopButtonNode of the same category.
                if (!nodeToBeFocused) {
                    nodeToBeFocused = categories[currentIndex].moveTopButtonNode;
                }

                that.categoryMoved({
                    action: "Bottom",
                    itemId: that.id,
                    newIndex: categories.length - 1,
                    originator: {source: "UI"}
                });

                nodeToBeFocused.focus();
            };

            this._resetConfigurationLayoutListeners();
            
            this.own(aspect.after(this, "addChild", function (widget, index, originator) {
                index = index >= 0 ? index : this.getChildren().length - 1;

                widget.set("displayState", this.displayState);
                widget.set("galleryItemRowCount", this.galleryItemRowCount);

                if (this.dndSource) {
                    this.dndSource.insertNodes(false, [widget.domNode], false,
                        this.getChildren()[index].domNode);
                }
                widget.favButtonNode.setAttribute("tabindex", "0");
                this.showHideCategory();

                // Only fire Gallery State Changed event when the state is changed from a user interaction
                if (originator && ["UI", "addGalleryItemToFavorites"].indexOf(originator.source) > -1) {
                    this.galleryStateChanged(true);
                } else {
                    this.galleryStateChanged(false);
                }
            }.bind(this), true));

            this.own(aspect.after(this, "removeChild", function (widget, originator) {
                this.showHideCategory();

                // Only fire Gallery State Changed event when the state is changed from a user interaction
                if (originator && ["UI", "removeGalleryItemFromFavorites"].indexOf(originator.source) > -1) {
                    this.galleryStateChanged(true);
                } else {
                    this.galleryStateChanged(false);
                }
            }.bind(this), true));

            var onMouseDown = this._onMouseDown.bind(this);
            this.domNode.addEventListener("mousedown", onMouseDown);
            this.own({remove: function () {this.domNode.removeEventListener("mousedown", onMouseDown);}.bind(this)});

            this.showHideCategory();

            this._updateElementTitles();
            this.titleNode.setAttribute("tabindex", "0");
            this.moveTopButtonNode.setAttribute("tabindex", "0");
            this.moveBottomButtonNode.setAttribute("tabindex", "0");
        },

        _updateElementTitles: function () {
            this.moveTopButtonNode.title = toolstripL10n.navTop;
            this.moveBottomButtonNode.title = toolstripL10n.navBottom;
        },

        _resetConfigurationLayoutListeners: function () {
            var that = this;
            if (that._removeConfigurationLayoutListeners) {
                that._removeConfigurationLayoutListeners();
            }
            // Attach a click handler to the moveTopButtonNode:
            that.moveTopButtonNode.addEventListener("click", that._moveToTopButtonClickHandler);
            that.moveBottomButtonNode.addEventListener("click", that._moveToBottomButtonClickHandler);
            that._removeConfigurationLayoutListeners = function () {
                if (that.moveTopButtonNode && that._moveToTopButtonClickHandler) {
                    that.moveTopButtonNode.removeEventListener("click", that._moveToTopButtonClickHandler);
                }
                if (that.moveBottomButtonNode && that._moveToBottomButtonClickHandler) {
                    that.moveBottomButtonNode.removeEventListener("click", that._moveToBottomButtonClickHandler);
                }
                delete that._removeConfigurationLayoutListeners;
            };
            that.own(
                {
                    remove: that._removeConfigurationLayoutListeners
                }
            );
        },

        showHideCategory: function (category) {
            if (!category) {
                category = this;
            }
            if (category.getChildren().length === 0) {
                if (category.get("hideWhenEmpty")) {
                    category.containerNode.textContent = "";
                    category.containerNode.classList.remove("mwEmptyNode");
                    category.domNode.classList.add("hideFromView");
                    return;
                } else {
                    category.containerNode.textContent = toolstripL10n.emptyCategoryPlaceholder;
                    category.containerNode.classList.add("mwEmptyNode");
                }
            } else {
                // Should only really play a roll when childNodes.length === 2
                if (category.containerNode.firstChild.nodeType === 3) { // Text
                    category.containerNode.removeChild(category.containerNode.firstChild);
                } else if (category.containerNode.lastChild.nodeType === 3) { // Text
                    category.containerNode.removeChild(category.containerNode.lastChild);
                }
                category.containerNode.classList.remove("mwEmptyNode");
            }

            category.domNode.classList.remove("hideFromView");
        },
        
        _setHideWhenEmptyAttr: function (value) {
            if (typeof value !== "boolean") {
                throw new Error("Expected input value to be of type 'boolean'.");
            }
            
            this._set("hideWhenEmpty", value);
        },
        
        _setDndEnabledAttr: function (value) {
            if (typeof value !== "boolean") {
                throw new Error("Expected input value to be of type 'boolean'.");
            }

            this._set("dndEnabled", value);

            if (value) {
                this._createDndSource();
            } else {
                this._destroyDndSource();
            }
        },

        _setIconAttr: function (value) {
            if (this.get("isFavoriteCategory")) {
                return;
            }

            if ((typeof value !== "string") && (CATEGORY_ICONS.indexOf(value) < 0)) {
                throw new Error("Expected input value to be of type 'string'.");
            }

            if (this.get("icon")) {
                this.titleIconNode.classList.remove(this.get("icon"));
            }

            if (value !== "") {
                this.titleIconNode.classList.add(value);
            }

            this._set("icon", value);
        },

        _setIsFavoriteCategoryAttr: function (value) {
            if (value) {
                if (this.get("icon")) {
                    this.titleIconNode.classList.remove(this.get("icon"));
                }
                this.titleIconNode.classList.add("starTitle");
            } else {
                this.titleIconNode.classList.remove("starTitle");
                if (this.get("icon")) {
                    this.titleIconNode.classList.add(this.get("icon"));
                }
            }
            this._set("isFavoriteCategory", value);

            this._createDndSource();
        },

        _setDisplayStateAttr: function (value) {
            this.getChildren().forEach(function (item) {
                item.set("displayState", value);
            });

            this._set("displayState", value);

            this._createDndSource();
        },

        /**
         * A setter for the label attribute.
         */
        _setTitleAttr: function(title) {
            if (title === "") {
                this.header.style.display = "none";
                this.domNode.title = "";
            } else {
                this.header.style.display = "flex";
                this.domNode.title = title;
            }

            this.titleNode.textContent = title;
            this._set("title", title);
        },
        
        _setTagAttr: function (tag) {
            if (typeof tag !== "string") {
                throw new Error('Expected inpute argument to be of type "string"');
            }
            
            this._set("tag", tag);
            this.domNode.dataset.tag = tag;
        },

        _setReorderCategoryAttr: function (value) {
            if (value === false) {
                this.controlsNode.style.display = "none";
            } else {
                this.controlsNode.style.display = null;
            }
        },

        _setGalleryItemRowCountAttr: function (value) {
            this._set("galleryItemRowCount", value);

            this.getChildren().forEach(function(child) {
                child.set("galleryItemRowCount", this.galleryItemRowCount);
            }.bind(this));
        },

        _onMouseDown: function () {
            if (this.dndSource) {
                this.dndSource.selectNone.call(this.dndSource);
            }
        },

        /**
         * Makes a DnD source out of the container node of this category and connects the
         * onDropHandler.
         */
        _createDndSource: function () {
            var aspectHandle, that = this, options = {};

            if (this.dndEnabled) {
                if (this.displayState === GalleryViewConstants.ICON) {
                    options = {horizontal: true};
                }

                // === Unregister category item container and category header as drop targets ===
                this._destroyDndSource();

                this._dndAspects = [];

                // === Register category item container as a drag and drop target ===
                // Makes the children of the containerNode draggable.
                this.dndSource = new Source(this.containerNode, options);
                DnDUtils.registerMakeAvatar(that.dndSource, function () {
                    return new GalleryItemAvatar(this);
                });

                // onDrop is triggered whenever an item is released after dragging inside the source.
                aspectHandle = aspect.around(this.dndSource, 'onDrop', function (originalMethod) {
                    return function (source, nodes) {
                        if ((source.node !== that.containerNode) &&
                            ((source.node.parentElement.dataset.type === "GalleryFavoriteCategory") ||
                            (that.domNode.dataset.type === "GalleryFavoriteCategory"))) {
                            // Do not allow drag and drop between Favorites Category and other Categories
                            return;
                        }
                        originalMethod.call(this, source, nodes);
                        that.dndSource.selectNone.call(that.dndSource);
                        that._onContainerDropHandler(source, nodes);
                        that.showHideCategory(registry.getEnclosingWidget(source.node));
                        that.showHideCategory();
                    }
                });
                this._dndAspects.push(aspectHandle);
                this.own(aspectHandle);

                // === Register category header as a drop target ===
                // Makes the header a drop target.
                this.dndSourceHeader = new Target(this.headerDropNode, {});
                DnDUtils.registerMakeAvatar(that.dndSourceHeader, function () {
                    return new GalleryItemAvatar(this);
                });

                aspectHandle = aspect.around(this.dndSourceHeader, 'onDrop', function (originalMethod) {
                    return function (source, nodes) {
                        that._onHeaderDropHandlerBefore(source, nodes);
                        if ((source.node !== that.containerNode) &&
                            ((source.node.parentElement.dataset.type === "GalleryFavoriteCategory") ||
                            (that.domNode.dataset.type === "GalleryFavoriteCategory"))) {
                            // Do not allow drag and drop between Favorites Category and other Categories
                            return;
                        }
                        originalMethod.call(this, source, nodes);
                        that.dndSourceHeader.selectNone.call(that.dndSourceHeader);
                        that._onHeaderDropHandlerAfter(source, nodes);
                        that.showHideCategory(registry.getEnclosingWidget(source.node));
                        that.showHideCategory();
                    }});
                this._dndAspects.push(aspectHandle);
                this.own(aspectHandle);

                aspectHandle = aspect.after(this.dndSourceHeader, 'onDraggingOver', function (source, nodes) {
                    that.headerDropNode.classList.add("mwHeaderDropNodeDragOver");
                }, true);
                this._dndAspects.push(aspectHandle);
                this.own(aspectHandle);

                aspectHandle = aspect.after(this.dndSourceHeader, 'onDraggingOut', function (source, nodes) {
                    that.headerDropNode.classList.remove("mwHeaderDropNodeDragOver");
                }, true);
                this._dndAspects.push(aspectHandle);
                this.own(aspectHandle);

            }
        },
        
        _destroyDndSource: function () {
            // === Cleanup DnD Aspects ===
            if (this._dndAspects) {
                this._dndAspects.forEach(function (aspectHandle) {
                    aspectHandle.remove();
                });
                delete this._dndAspects;
            }

            // === Unregister category item container as a drag and drop target ===
            if (this.dndSource) {
                DnDUtils.unregisterMakeAvatar(this.dndSource);
                this.dndSource.destroy();
                delete this.dndSource;
            }

            // === Unregister category header as a drop target ===
            if (this.dndSourceHeader) {
                DnDUtils.unregisterMakeAvatar(this.dndSourceHeader);
                this.dndSourceHeader.destroy();
                delete this.dndSourceHeader;
            }
        },

        onDrop: function () {
            this.emit("drop");
        },

        /**
         * Callback attached to the 'onDrop' event of the Source. Updates the model according to the
         * new order of items in the view after the drag and drop has occurred.
         * @param source The source of dragged items, can be the same object as the
         * target.
         * @param nodes The array of DOM nodes to be dropped.
         */
        _onContainerDropHandler: function (source, nodes) {
            // Although dojo supports the ability to drag multiple items at a time. We only intend
            // to support the dragging of a single item at a time. Since, nodes is an array of
            // dragged nodes, we are selecting the first and only node that has been dragged.
            var node = nodes[0], newIndex, movedItemWidget;

            movedItemWidget = registry.getEnclosingWidget(node);
            newIndex = this.getIndexOfChild(movedItemWidget);
            this.itemMoved({
                itemId: movedItemWidget.id,
                parentId: this.id,
                newIndex: newIndex,
                originator: {source: "UI"}
            }, {
                widget: movedItemWidget,
                sourceData: source,
                targetWidget: this
            });
        },

        _onHeaderDropHandlerBefore: function (source, nodes) {
            this.headerDropNode.classList.remove("mwHeaderDropNodeDragOver");

            // In order to force dojo/dnd/Source.js, dojo/dnd/Selector.js, and dojo/dnd/Container.js
            // to place the dropped item in the containerNode, we need to give these files the
            // inputs they need in order to think the drop target was in the containerNode.
            this.dndSourceHeader.before = true;
            this.dndSourceHeader.current = this.containerNode.firstChild;
        },

        _onHeaderDropHandlerAfter: function (source, nodes) {
            // Although dojo supports the ability to drag multiple items at a time. We only intend
            // to support the dragging of a single item at a time. Since, nodes is an array of
            // dragged nodes, we are selecting the first and only node that has been dragged.
            var node = nodes[0], newIndex, movedItemWidget;

            nodes.forEach(function (node) {
                var t = this.dndSourceHeader._normalizedCreator(node);
                this.dndSourceHeader.delItem(t.node.id);
                this.dndSource.setItem(t.node.id, {data: t.data, type: t.type});
            }.bind(this));

            movedItemWidget = registry.getEnclosingWidget(node);

            if (this.displayState === GalleryViewConstants.ICON) {
                newIndex = this.getChildren().length;
            } else {
                newIndex = 0;
            }

            this.itemMoved({
                itemId: movedItemWidget.id,
                parentId: this.id,
                newIndex: newIndex,
                originator: {source: "UI"}
            }, {
                widget: movedItemWidget,
                sourceData: source,
                targetWidget: this
            });
        },

        _qeMoveToTopClick: function () {
            this._moveToTopButtonClickHandler();
        },

        _qeMoveToBottomClick: function () {
            this._moveToBottomButtonClickHandler();
        },

        galleryStateChanged: function (fireEventOnGalleryPopupOrPanel) {
            var parentWidget = this.getParent();

            if (parentWidget) {
                if (fireEventOnGalleryPopupOrPanel) {
                    if (parentWidget.galleryStateChanged) {
                        parentWidget.galleryStateChanged();
                    }
                } else if (parentWidget.refreshGallery) {
                    parentWidget.refreshGallery();
                }
            }
        },

        categoryMoved: function (data) {
            this.emit("categorymoved", {data: data});
        },

        itemMoved: function (data, additionalData) {
            this.emit("itemmoved", {
                mwEventData: {
                    widget: additionalData.widget,
                    parent: additionalData.targetWidget,
                    index: data.newIndex,
                    originator: data.originator
                }
            });
        }

    });
});